/**
 * Calculate the balance remaining after the current term.
 */
const calculateBalanceRemaining = (
  rate: number,
  loanAmount: number,
  mortgagePayment: number,
  paymentFrequency: string,
  termPayments: number
) => {
  const compoundingRate = calculatePeriodicRate(rate / 100, paymentFrequency);
  return (
    loanAmount * (1 + compoundingRate) ** termPayments -
    mortgagePayment * (((1 + compoundingRate) ** termPayments - 1) / compoundingRate)
  );
};

/**
 * Calculates the borrowing amount from the purchase price and down payment.
 */
const calculateBorrowingAmount = (purchasePrice: number, downPayment: number) => purchasePrice - downPayment;

/**
 * Calculates estimated broker compensation from the loan amount and bps.
 */
const calculateCompensation = (loanAmount: number, bps: number) => (loanAmount * bps) / 10000;

/**
 * Calculates the insurance amount based on the purchase price, down payment, and insurability of the product.
 */
const calculateInsuranceAmount = (
  amortization: number,
  borrowingAmount: number,
  insurable: string,
  insurance: string,
  insuranceActive: boolean,
  ltv: number,
  refinanceOnly: boolean
) => {
  const amortizationSurcharge = amortization > 25 ? 0.002 : 0;

  switch (true) {
    case insurable === 'No':
    case insurance === 'Conventional' && !insuranceActive:
    case refinanceOnly:
    case ltv > 95:
      return 0;
    case ltv > 90:
      return (0.04 + amortizationSurcharge) * borrowingAmount;
    case ltv > 85:
      return (0.031 + amortizationSurcharge) * borrowingAmount;
    case ltv > 80:
      return (0.028 + amortizationSurcharge) * borrowingAmount;
    case ltv > 75:
      return (0.024 + amortizationSurcharge) * borrowingAmount;
    case ltv > 65:
      return (0.017 + amortizationSurcharge) * borrowingAmount;
    default:
      return (0.006 + amortizationSurcharge) * borrowingAmount;
  }
};

/**
 * Determines whether a scenario is eligible for insurance.
 */
const calculateinsuranceStatus = (insurable: string, insurance: string, ltv: number, refinanceOnly: boolean) => {
  switch (true) {
    case insurable === 'No':
    case refinanceOnly:
    case ltv > 95:
      return 'uninsurable';
    case insurance === 'Insured':
      return 'insured';
    default:
      return 'insurable';
  }
};

/**
 * Calculates the borrowing amount and adds insurance premiums if applicable.
 */
const calculateLoanAmount = (borrowingAmount: number, insuranceAmount: number) => borrowingAmount + insuranceAmount;

/**
 * Calculates the LTV from the mortgage amount and property value.
 */
const calculateLtv = (mortgageAmount: number, propertyValue: number) => (mortgageAmount / propertyValue) * 100;

/**
 * Calculates the maximum monthly payment from annual income and GDS.
 */
const calculateMaxMonthlyPayment = (annualIncome = 0, gds = 39) => (annualIncome * (gds / 100)) / 12;

/**
 * Calculates the mortgage payment from the borrowing amount and present value factor.
 */
const calculateMonthlyPayment = (loanPayments = 0, maxMonthlyPayment = 0, creditCardBalance = 0) =>
  maxMonthlyPayment - loanPayments - creditCardBalance * 0.03;

/**
 * Calculates the mortgage payment from the amortization, loan amount, rate, and payment frequency.
 */
const calculateMortgagePayment = (amortization: number, loanAmount: number, paymentFrequency: string, rate: number) => {
  const monthlyRate = calculatePeriodicRate(rate / 100, 'Monthly');
  const monthlyPayments = getTotalPayments('Monthly', amortization);
  const monthlyPayment = (loanAmount * monthlyRate) / (1 - (1 + monthlyRate) ** -monthlyPayments);
  const periodicRate = calculatePeriodicRate(rate / 100, paymentFrequency);
  const totalPayments = getTotalPayments(paymentFrequency, amortization);
  switch (paymentFrequency) {
    case 'Accelerated bi-weekly':
      return monthlyPayment / 2;
    case 'Accelerated weekly':
      return monthlyPayment / 4;
    default:
      return (loanAmount * periodicRate) / (1 - (1 + periodicRate) ** -totalPayments);
  }
};

const calculateMonthlyEquivalentPayment = (mortgagePayment: number, paymentFrequency: string) => {
  switch (paymentFrequency) {
    case 'Bi-weekly':
    case 'Accelerated bi-weekly':
      return (mortgagePayment * 26) / 12;
    case 'Weekly':
    case 'Accelerated weekly':
      return (mortgagePayment * 52) / 12;
    case 'Semi-monthly':
      return mortgagePayment * 2;
    default:
      return mortgagePayment;
  }
};

/**
 * Calculates total mortgage affordability from annual income and qualifying rate.
 */
const calculateTotalMortgageAffordability = (
  annualIncome = 0,
  qualifyingRate = 0,
  loanPayments = 0,
  creditCardBalance = 0,
  amortization = 30,
  gds = 39
) => {
  if (!annualIncome || !qualifyingRate) {
    return 0;
  }

  const compoundQualifyingRate = calculatePeriodicRate(qualifyingRate / 100, 'Monthly');
  const presentValueFactor = calculatePresentValueFactor(compoundQualifyingRate, amortization);
  const presentValueInterestFactorOfAnnuity = calculatePresentValueInterestFactorOfAnnuity(
    presentValueFactor,
    compoundQualifyingRate
  );
  const maxMonthlyPayment = calculateMaxMonthlyPayment(annualIncome, gds);
  const monthlyPayment = calculateMonthlyPayment(loanPayments, maxMonthlyPayment, creditCardBalance);
  return monthlyPayment * presentValueInterestFactorOfAnnuity;
};

/**
 * Calculates the total sum of a specific property for a list of borrowers.
 */
const calculateBorrowersTotal = (borrowers: Record<string, unknown>[] = [], propertyName: string) =>
  borrowers.reduce((total, borrower) => total + (parseFloat(borrower[propertyName] as string) || 0), 0);

/**
 * Calculates interest paid over a term.
 */
const calculateTermInterest = (
  mortgagePayment: number,
  paymentFrequency: string,
  term: string,
  termPrincipal: number
) => {
  const termPayments = getTermPayments(paymentFrequency, term);
  return mortgagePayment * termPayments - termPrincipal;
};

/**
 * Calculates the principal paid over a term.
 */
const calculateTermPrincipal = (balanceRemaining: number, loanAmount: number) => loanAmount - balanceRemaining;

/**
 * Computes all mortgage details for a scenario from product and scenario parameters.
 */
const calculateScenarioDetails = (
  amortization: number,
  bps: number,
  downPayment: number,
  insurable: string,
  insurance: string,
  insuranceActive: boolean,
  paymentFrequency: string,
  purchasePrice: number,
  rate: number,
  refinanceOnly: boolean,
  term: string
) => {
  const borrowingAmount = calculateBorrowingAmount(purchasePrice, downPayment);
  const ltv = calculateLtv(borrowingAmount, purchasePrice);
  const insuranceAmount = calculateInsuranceAmount(
    amortization,
    borrowingAmount,
    insurable,
    insurance,
    insuranceActive,
    ltv,
    refinanceOnly
  );
  const insuranceStatus = calculateinsuranceStatus(insurable, insurance, ltv, refinanceOnly);
  const loanAmount = calculateLoanAmount(borrowingAmount, insuranceAmount);
  const compensation = calculateCompensation(loanAmount, bps);
  const termPayments = getTermPayments(paymentFrequency, term);
  const mortgagePayment = calculateMortgagePayment(amortization, loanAmount, paymentFrequency, rate);
  const balanceRemaining = calculateBalanceRemaining(rate, loanAmount, mortgagePayment, paymentFrequency, termPayments);
  const termPrincipal = calculateTermPrincipal(balanceRemaining, loanAmount);
  const termInterest = calculateTermInterest(mortgagePayment, paymentFrequency, term, termPrincipal);
  const monthlyEquivalentPayment = calculateMonthlyEquivalentPayment(mortgagePayment, paymentFrequency);
  return {
    balanceRemaining,
    borrowingAmount,
    compensation,
    insuranceAmount,
    insuranceStatus,
    loanAmount,
    monthlyEquivalentPayment,
    mortgagePayment,
    termInterest,
    termPrincipal,
  };
};

/**
 * Calculates the effective rate from the nominal rate.
 */
const calculateEffectiveRate = (nominalRate: number, compoundingFrequency = 2) =>
  (nominalRate / compoundingFrequency + 1) ** compoundingFrequency - 1;

/**
 * Calculates the effective periodic rate from the nominal rate.
 */
const calculatePeriodicRate = (nominalRate: number, paymentFrequency: string) => {
  switch (paymentFrequency) {
    case 'Monthly':
      return (1 + calculateEffectiveRate(nominalRate)) ** (1 / 12) - 1;
    case 'Semi-monthly':
      return (1 + calculateEffectiveRate(nominalRate)) ** (1 / 24) - 1;
    case 'Bi-weekly':
    case 'Accelerated bi-weekly':
      return (1 + calculateEffectiveRate(nominalRate)) ** (1 / 26) - 1;
    case 'Weekly':
    case 'Accelerated weekly':
      return (1 + calculateEffectiveRate(nominalRate)) ** (1 / 52) - 1;
    default:
      return 0;
  }
};

/**
 * Calculates the present value factor from the rate and amortization in years.
 */
const calculatePresentValueFactor = (rate: number, amortization: number) => 1 / (1 + rate) ** (amortization * 12);

/**
 * Calculates the present value interest factor of annuity.
 */
const calculatePresentValueInterestFactorOfAnnuity = (presentValueFactor: number, rate: number) =>
  (1 - presentValueFactor) / rate;

/**
 * Get the number of months for a given term.
 */
const getTermInMonths = (term: string) => {
  switch (term) {
    case '6 Months':
      return 6;
    case '1 Year':
      return 12;
    case '2 Year':
      return 24;
    case '3 Year':
      return 36;
    case '4 Year':
      return 48;
    case '5 Year':
      return 60;
    case '6 Year':
      return 72;
    case '7 Year':
      return 84;
    case '10 Year':
      return 120;
    default:
      return 0;
  }
};

/**
 * Get the total number of payments for a given payment frequency and term.
 */
const getTermPayments = (paymentFrequency: string, term: string) => {
  const termInMonths = getTermInMonths(term);
  switch (paymentFrequency) {
    case 'Monthly':
      return termInMonths;
    case 'Semi-monthly':
      return termInMonths * 2;
    case 'Bi-weekly':
      return termInMonths * (26 / 12);
    case 'Accelerated bi-weekly':
      return termInMonths * (26 / 12);
    case 'Weekly':
      return termInMonths * (52 / 12);
    case 'Accelerated weekly':
      return termInMonths * (52 / 12);
    default:
      return 0;
  }
};

/**
 * Gets the total number of payments for a given payment frequency and amortization.
 */
const getTotalPayments = (paymentFrequency: string, amortization: number) => {
  switch (paymentFrequency) {
    case 'Monthly':
      return amortization * 12;
    case 'Semi-monthly':
      return amortization * 24;
    case 'Bi-weekly':
    case 'Accelerated bi-weekly':
      return amortization * 26;
    case 'Weekly':
    case 'Accelerated weekly':
      return amortization * 52;
    default:
      return 0;
  }
};

export default {
  borrowersDetailsTotal: calculateBorrowersTotal,
  borrowingAmount: calculateBorrowingAmount,
  ltv: calculateLtv,
  maxMonthlyPayment: calculateMaxMonthlyPayment,
  scenarioDetails: calculateScenarioDetails,
  totalMortgageAffordability: calculateTotalMortgageAffordability,
};
